import type { InferResponseType } from 'hono/client'

import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { PageTitle } from '@/components/layout/page-title'
import { apiClient } from '@/apiClient'

import { AttachmentsTab } from './attachments-tab'
import { DocumentDetails } from './document-details'
import { DocumentForm } from './document-form'
import { LogsTab } from './logs-tab'
import { adaptDocumentWithDetailsToDocument } from './type-adapter'

// Type for the single document response from the API
type DocumentWithDetails = InferResponseType<
  (typeof apiClient.documents)[':documentId']['$get']
>['data']

interface DesktopDocumentViewProps {
  document: DocumentWithDetails | undefined
  mode: 'view' | 'modify'
  tab: string
  onTabChange: (tab: string) => void
  previewContent: React.ReactNode
}

export function DesktopDocumentView({
  document,
  mode,
  tab,
  onTabChange,
  previewContent,
}: DesktopDocumentViewProps) {
  return (
    <div className="grid h-full grow grid-cols-2 overflow-y-hidden overflow-x-visible">
      {/* Left column */}
      <ScrollArea className="flex flex-col overflow-hidden">
        <div className="p-4">
          <PageTitle className="mb-2">
            {mode === 'view' ? document?.name : 'Modifica Documento'}
          </PageTitle>

          {mode === 'view' && document && (
            <DocumentDetails
              document={adaptDocumentWithDetailsToDocument(document)}
            />
          )}
          {mode === 'modify' && document && (
            <DocumentForm
              document={adaptDocumentWithDetailsToDocument(document)}
            />
          )}
        </div>
      </ScrollArea>

      {/* Right column */}
      <div className="bg-sidebar flex h-full flex-col overflow-y-hidden border-l p-2 px-4">
        <Tabs
          className="flex h-full flex-col overflow-hidden pr-4"
          value={tab}
          onValueChange={onTabChange}
        >
          <div className="text-center">
            <TabsList className="mx-auto">
              <TabsTrigger value="preview">Anteprima</TabsTrigger>
              <TabsTrigger value="attachments">
                Allegati
                {!!document?._count?.attachments &&
                  `  (${document?._count?.attachments})`}
              </TabsTrigger>
              <TabsTrigger value="logs">Logs</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent
            className={cn(
              'grow flex-col overflow-y-auto p-0',
              tab === 'preview' && 'flex'
            )}
            value="preview"
          >
            {previewContent}
          </TabsContent>

          <TabsContent
            className={cn(
              'grow flex-col overflow-y-auto p-0',
              tab === 'attachments' && 'flex'
            )}
            value="attachments"
          >
            {document && (
              <AttachmentsTab
                document={adaptDocumentWithDetailsToDocument(document)}
              />
            )}
          </TabsContent>

          <TabsContent
            className={cn(
              'grow flex-col overflow-y-auto p-0',
              tab === 'logs' && 'flex'
            )}
            value="logs"
          >
            {document && (
              <LogsTab
                document={adaptDocumentWithDetailsToDocument(document)}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}
